import { CommonModule } from "@angular/common";
import { HttpErrorResponse } from "@angular/common/http";
import { Component, OnInit, inject } from "@angular/core";
import {
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from "@angular/material/dialog";
import { Router } from "@angular/router";
import { UserInfo } from "@models/user";
import { TranslateModule } from "@ngx-translate/core";
import { finalize, tap } from "rxjs";
import { FinishSignUpFormComponent } from "../../../app/pages/finish-sign-up-page/components/finish-sign-up-form/finish-sign-up-form.component";
import { AuthService } from "../../../app/services/auth.service";
import { LocalStorageService } from "../../../app/services/local-storage.service";
import { SnackBarService } from "../../../app/services/snack-bar.service";
import { SignupRequestBody } from "../../../app/services/types/signup-request.type";
import { SignupResponseBody } from "../../../app/services/types/signup-response.type";
import { countriesList } from "../../../utils/constants/countries.constant";
import { EventLocationState } from "../../../utils/constants/event-location-state-brazil.constant";
import { ButtonComponent } from "../button/button.component";
import { ModalConfirmationComponent } from "../modal-confirmation/modal-confirmation.component";
import { ModalLoginComponent } from "../modal-login/modal-login.component";
import { PageHeaderComponent } from "../page-header/page-header.component";

@Component({
  selector: "app-modal-signup",
  imports: [
    CommonModule,
    TranslateModule,
    MatDialogModule,
    ButtonComponent,
    PageHeaderComponent,
    FinishSignUpFormComponent,
  ],
  templateUrl: "./modal-signup.component.html",
  styleUrl: "./modal-signup.component.scss",
})
export class ModalSignupComponent implements OnInit {
  errorMessage = "";
  isLoggedIn = false;
  userData?: UserInfo;

  brazilianStates = EventLocationState;
  countriesList = countriesList;
  isLoading = false;

  readonly dialogRef = inject(MatDialogRef<ModalSignupComponent>);
  private readonly dialog = inject(MatDialog);

  constructor(
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly snackbarService: SnackBarService,
    private localStorageService: LocalStorageService,
  ) {}

  ngOnInit() {
    this.checkLoginStatus();
  }

  private checkLoginStatus() {
    this.isLoggedIn = !!this.localStorageService.getItem("auth-token");

    if (this.isLoggedIn) {
      this.getUserData();
    }
  }

  handleFormSubmit(signupRequest: SignupRequestBody) {
    this.isLoading = true;
    if (this.isLoggedIn) {
      this.updateUser(signupRequest);
    } else {
      this.createUser(signupRequest);
    }
  }

  private createUser(signupRequest: SignupRequestBody) {
    const singupBody: SignupRequestBody = {
      user: {
        ...signupRequest.user,
        invited: false,
      },
    };

    this.authService
      .signup(singupBody)
      .pipe(
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe({
        next: this.handleSignupSuccess.bind(this),
        error: this.handleSignupError.bind(this),
      });
  }

  private updateUser(signupRequest: SignupRequestBody) {
    const updateUserBody: SignupRequestBody = {
      user: {
        ...signupRequest.user,
        roles: this.userData?.roles ?? [],
      },
    };

    this.authService
      .updateUserInfo(updateUserBody)
      .pipe(
        tap(() => {
          this.dialogRef.close();
          setTimeout(() => window.location.reload(), 1000);
        }),
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe({
        next: this.handleSignupSuccess.bind(this),
        error: this.handleSignupError.bind(this),
      });
  }

  private getUserData() {
    const userInfo = JSON.parse(
      this.localStorageService.getItem("current_user") ?? "false",
    );

    if (userInfo) {
      this.userData = userInfo;
    }
  }

  continuarComFacebook() {
    this.dialogRef.close();
  }

  continuarComGoogle() {
    this.dialogRef.close();
  }

  navigateToTermsOUse() {
    this.router.navigate(["/"]);
    this.dialogRef.close();
  }

  navigateToPrivacyPolicies() {
    this.router.navigate(["/"]);
    this.dialogRef.close();
  }

  private handleSignupSuccess(response: SignupResponseBody) {
    if (response.status.code === 200) {
      this.snackbarService.openSnackBar(
        "Cadastro efetuado com sucesso!",
        "Fechar",
        "success",
      );
      const userId = response?.data?.id;
      const userEmail = response?.data.email;
      if (!userId || !userEmail) {
        this.dialogRef.close();
        return;
      }
      this.openModalConfirmation(userId, userEmail);
      this.dialogRef.close();
    } else {
      this.errorMessage = "Credenciais inválidas. Por favor, tente novamente.";
    }
  }

  private handleSignupError(error: HttpErrorResponse) {
    this.errorMessage =
      "Não foi possível criar o usuário. O e-mail já esta cadastrado!";
    this.snackbarService.openSnackBar(this.errorMessage, "Fechar", "error");
    console.error(error);
  }

  openModalLogin() {
    this.dialog.open(ModalLoginComponent, {
      width: "500px",
      maxWidth: "95vw",
      maxHeight: "90dvh",
    });
    setTimeout(() => {
      this.dialogRef.close();
    }, 500);
  }

  openModalConfirmation(userId: number, email: string) {
    const dialogRef = this.dialog.open(ModalConfirmationComponent, {
      width: "600px",
      data: {
        userId: userId,
        userEmail: email,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      console.log("Modal fechado!", result);
    });
  }
}
